import {
  useCallback,
  useEffect,
  useMemo,
  useState,
  type Dispatch,
  type ReactElement,
  type SetStateAction,
} from 'react';
import { gridPaths, queryKeys } from '@/shared/hooks';
import { Stack } from '@/ui';
import { type GridColumnVisibilityModel } from '@/ui/primitives';
import { TestsTableV2 } from '../components/TestsTableV2';
import type { TestStatusKey } from '@/features/quality/components/qualityStatusDataHelpers';
import type { TableQueryMeta } from '@/features/shared/components/tableSystem/tableToolbar.helpers';
import type { DateQuickFilter } from '@/features/shared/hooks/useDateWindow';
import type {
  TestRunSortDirection,
  TestRunSortKey,
} from '@/features/tests/pages/testRunsTableDefinition';
import type { TestSortDirection, TestSortKey } from '@/features/tests/pages/testsTableDefinition';
import type { TagOption } from '@/shared/data/tags';
import type { ApiSort, GridQueryInput } from '@/ui/composites/grid/gridUtils';
import type { QueryResult, TestRunDto, TestRunStatus, TestListItemDto } from '@flowqa/shared';
import type { Dayjs } from 'dayjs';

type TabbedTestsPanelTab = 'tests' | 'executions';
type TestsTableHeaderSortKey = Extract<
  TestSortKey,
  | 'displayName'
  | 'executionAverageDurationMs'
  | 'executionFlakeScore'
  | 'executionFrequencyPerDay'
  | 'executionPassRate'
  | 'latestRunTimestamp'
>;

const isTestsTableHeaderSortKey = (field: string): field is TestsTableHeaderSortKey =>
  field === 'latestRunTimestamp' ||
  field === 'displayName' ||
  field === 'executionPassRate' ||
  field === 'executionFrequencyPerDay' ||
  field === 'executionAverageDurationMs' ||
  field === 'executionFlakeScore';

export type TestsPageTabbedPanelProps = {
  panelActiveTab: TabbedTestsPanelTab;
  setPanelActiveTab: Dispatch<SetStateAction<TabbedTestsPanelTab>>;
  panelDateQuickFilter: DateQuickFilter;
  setPanelDateQuickFilter: Dispatch<SetStateAction<DateQuickFilter>>;
  panelFromDate: Dayjs | null;
  setPanelFromDate: Dispatch<SetStateAction<Dayjs | null>>;
  panelToDate: Dayjs | null;
  setPanelToDate: Dispatch<SetStateAction<Dayjs | null>>;
  panelColumnVisibilityModel: GridColumnVisibilityModel;
  setPanelColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  panelSortKey: TestSortKey;
  setPanelSortKey: Dispatch<SetStateAction<TestSortKey>>;
  panelSortDirection: TestSortDirection;
  setPanelSortDirection: Dispatch<SetStateAction<TestSortDirection>>;
  panelFilterStatuses: TestRunStatus[];
  setPanelFilterStatuses: Dispatch<SetStateAction<TestRunStatus[]>>;
  panelFilterTests: string[];
  setPanelFilterTests: Dispatch<SetStateAction<string[]>>;
  panelFilterSuites: string[];
  setPanelFilterSuites: Dispatch<SetStateAction<string[]>>;
  panelFilterTags: string[];
  setPanelFilterTags: Dispatch<SetStateAction<string[]>>;
  panelExecutionColumnVisibilityModel: GridColumnVisibilityModel;
  setPanelExecutionColumnVisibilityModel: Dispatch<SetStateAction<GridColumnVisibilityModel>>;
  panelExecutionSortKey: TestRunSortKey;
  setPanelExecutionSortKey: Dispatch<SetStateAction<TestRunSortKey>>;
  panelExecutionSortDirection: TestRunSortDirection;
  setPanelExecutionSortDirection: Dispatch<SetStateAction<TestRunSortDirection>>;
  panelAvailableTags: string[];
  suiteOptions: Array<{ value: string; label: string }>;
  testOptions: Array<{ value: string; label: string }>;
  panelDateWindow: { startDay: string | null; endDay: string | null; sparklineRangeDays: number };
  panelStatusFilterKey: string;
  panelTestFilterKey: string;
  panelSuiteFilterKey: string;
  panelTagFilterKey: string;
  panelNoRowsText: string;
  panelExecutionsNoRowsText: string;
  forceLoading?: boolean;
  debouncedPanelSearch: string;
  panelTestsMeta: TableQueryMeta<TestListItemDto> | null;
  setPanelTestsMeta: Dispatch<SetStateAction<TableQueryMeta<TestListItemDto> | null>>;
  panelTestRunsMeta: TableQueryMeta<TestRunDto> | null;
  setPanelTestRunsMeta: Dispatch<SetStateAction<TableQueryMeta<TestRunDto> | null>>;
  panelTestRunDateBounds: { from: Dayjs | null; to: Dayjs | null };
  showPanelDeltas: boolean;
  selectedOrganisationId: string | null;
  selectedProjectId: string | null;
  handleSearchChange: (value: string) => void;
  handleRangeChange: (value: DateQuickFilter) => void;
  handleSelectedStartDayChange: (value: string | null) => void;
  handleSelectedEndDayChange: (value: string | null) => void;
  handleStatusesChange: (value: TestStatusKey[]) => void;
  handleTagsChange: (value: string[]) => void;
  handleDebouncedPanelSearchChange: (value: string) => void;
  panelQueryFn: (input: GridQueryInput) => Promise<QueryResult<TestListItemDto>>;
  panelExecutionQueryFn: (input: GridQueryInput) => Promise<QueryResult<TestRunDto>>;
  tagOptionsOverride: TagOption[];
};

export const TestsPageTabbedPanel = ({
  panelActiveTab: _panelActiveTab,
  setPanelActiveTab: _setPanelActiveTab,
  panelDateQuickFilter,
  setPanelDateQuickFilter: _setPanelDateQuickFilter,
  panelFromDate: _panelFromDate,
  setPanelFromDate: _setPanelFromDate,
  panelToDate: _panelToDate,
  setPanelToDate: _setPanelToDate,
  panelColumnVisibilityModel: _panelColumnVisibilityModel,
  setPanelColumnVisibilityModel: _setPanelColumnVisibilityModel,
  panelSortKey,
  setPanelSortKey: _setPanelSortKey,
  panelSortDirection,
  setPanelSortDirection: _setPanelSortDirection,
  panelFilterStatuses: _panelFilterStatuses,
  setPanelFilterStatuses: _setPanelFilterStatuses,
  panelFilterTests: _panelFilterTests,
  setPanelFilterTests: _setPanelFilterTests,
  panelFilterSuites: _panelFilterSuites,
  setPanelFilterSuites: _setPanelFilterSuites,
  panelFilterTags: _panelFilterTags,
  setPanelFilterTags: _setPanelFilterTags,
  panelExecutionColumnVisibilityModel: _panelExecutionColumnVisibilityModel,
  setPanelExecutionColumnVisibilityModel: _setPanelExecutionColumnVisibilityModel,
  panelExecutionSortKey: _panelExecutionSortKey,
  setPanelExecutionSortKey: _setPanelExecutionSortKey,
  panelExecutionSortDirection: _panelExecutionSortDirection,
  setPanelExecutionSortDirection: _setPanelExecutionSortDirection,
  panelAvailableTags: _panelAvailableTags,
  suiteOptions: _suiteOptions,
  testOptions: _testOptions,
  panelDateWindow,
  panelStatusFilterKey,
  panelTestFilterKey,
  panelSuiteFilterKey,
  panelTagFilterKey,
  panelNoRowsText,
  panelExecutionsNoRowsText: _panelExecutionsNoRowsText,
  forceLoading = false,
  debouncedPanelSearch,
  panelTestsMeta: _panelTestsMeta,
  setPanelTestsMeta,
  panelTestRunsMeta: _panelTestRunsMeta,
  setPanelTestRunsMeta: _setPanelTestRunsMeta,
  panelTestRunDateBounds: _panelTestRunDateBounds,
  showPanelDeltas,
  selectedOrganisationId,
  selectedProjectId,
  handleSearchChange: _handleSearchChange,
  handleRangeChange: _handleRangeChange,
  handleSelectedStartDayChange: _handleSelectedStartDayChange,
  handleSelectedEndDayChange: _handleSelectedEndDayChange,
  handleStatusesChange: _handleStatusesChange,
  handleTagsChange: _handleTagsChange,
  handleDebouncedPanelSearchChange: _handleDebouncedPanelSearchChange,
  panelQueryFn,
  panelExecutionQueryFn: _panelExecutionQueryFn,
  tagOptionsOverride,
}: TestsPageTabbedPanelProps): ReactElement => {
  const [tableSortKey, setTableSortKey] = useState<TestSortKey>(panelSortKey);
  const [tableSortDirection, setTableSortDirection] =
    useState<TestSortDirection>(panelSortDirection);

  useEffect(() => {
    setTableSortKey(panelSortKey);
  }, [panelSortKey]);

  useEffect(() => {
    setTableSortDirection(panelSortDirection);
  }, [panelSortDirection]);

  const testsQueryKey = useMemo(
    () => [
      ...queryKeys.dataGrid(gridPaths.tests),
      panelDateQuickFilter,
      selectedOrganisationId ?? null,
      selectedProjectId ?? null,
      showPanelDeltas ? 'deltas-on' : 'deltas-off',
      tableSortKey,
      tableSortDirection,
      panelStatusFilterKey,
      panelTestFilterKey,
      panelSuiteFilterKey,
      panelTagFilterKey,
      panelDateWindow.startDay,
      panelDateWindow.endDay,
    ],
    [
      panelDateQuickFilter,
      panelDateWindow.endDay,
      panelDateWindow.startDay,
      panelStatusFilterKey,
      panelSuiteFilterKey,
      panelTagFilterKey,
      panelTestFilterKey,
      selectedOrganisationId,
      selectedProjectId,
      showPanelDeltas,
      tableSortDirection,
      tableSortKey,
    ],
  );
  const handleTestsQueryMeta = useCallback(
    (meta: TableQueryMeta<TestListItemDto> | null) => {
      setPanelTestsMeta(meta);
    },
    [setPanelTestsMeta],
  );

  const tableQueryFn = useCallback(
    async (input: GridQueryInput): Promise<QueryResult<TestListItemDto>> =>
      await panelQueryFn({
        ...input,
        sorts: [{ field: tableSortKey, direction: tableSortDirection }],
      }),
    [panelQueryFn, tableSortDirection, tableSortKey],
  );
  const handleTableHeaderSortChange = useCallback((nextSort: ApiSort): void => {
    if (!isTestsTableHeaderSortKey(nextSort.field)) return;
    setTableSortKey(nextSort.field);
    setTableSortDirection(nextSort.direction);
  }, []);

  return (
    <Stack gap="xl">
      <TestsTableV2
        active
        forceLoading={forceLoading}
        queryKey={testsQueryKey}
        queryFn={tableQueryFn}
        search={debouncedPanelSearch}
        selectedProjectId={selectedProjectId}
        sort={{ field: tableSortKey, direction: tableSortDirection }}
        noRowsText={panelNoRowsText}
        tagOptionsOverride={tagOptionsOverride}
        onQueryMeta={handleTestsQueryMeta}
        onSortChange={handleTableHeaderSortChange}
      />
    </Stack>
  );
};
